import style from "@/styles/pages/Address/Address.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import ManualAddress from "@/components/Address/ManualAddress";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectApplication, setApplicationProgress } from "@/slices/applicationSlice";
import { RootState } from "@/store/dtm";
import { addPreviousAddress, addressInfo, removePreviousAddress, selectPreviousAddress, setAddressInfo } from "@/slices/PreviousAddressSlice";
import React from "react";
import SlideTransition from "@/styles/animation/SlideTransition";
import { AnimatePresence, motion } from "framer-motion";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { createApiClient } from "@/lib/Requests";
import { AddressDto, ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function JointPreviousAddress() {
    const navigate = useNavigate();

    const [addressSelectionError, setAddressSelectionError] = useState(false);
    const [displayError, setDisplayError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const dispatch = useDispatch();
    const { state } = useLocation();
    const homeAddress = useSelector(
        (state: RootState) => state.homeAddress.address
    );
    const previousAddresses = useSelector(selectPreviousAddress).addresses;
    const application = useSelector(selectApplication).application;
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });
    const [editMode, setEditMode] = useState(false);

    const [addressErrors, setAddressErrors] = useState<string[][]>([]);

    const methods = useForm({
        defaultValues: {
            autoAddress: [],
            streetNumber: [],
            streetName: [],
            streetType: [],
            streetDirection: [],
            city: [],
            province: [],
            country: [],
            postalCode: [],
            unitType: [],
            unitNumber: [],
            manualAddressEntered: [],
            yearsLivedOn: [],
            monthsLivedOn: []
        },
    });

    const fields = [
        {
            element: "search",
            field: "",
            mode: window.pca?.fieldMode.SEARCH,
        },
        {
            element: "span-street-address",
            field: "Line1",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-street-address2",
            field: "Line2",
            mode: window.pca?.fieldMode?.POPULATE,
        },
        { element: "city", field: "City", mode: window.pca?.fieldMode.POPULATE },
        {
            element: "span-state",
            field: "ProvinceCode",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-postcode",
            field: "PostalCode",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-country",
            field: "CountryName",
            mode: window.pca?.fieldMode.POPULATE,
        },
    ];
    const options = {
        key: "XD95-YY77-BP99-EM55",
    };

    const toggleManualAddress = (id: string) => {
        // setmanualAddressShown(!manualAddressShown);
        // if (!manualAddressShown) {
        //     formatAddress();
        //     setShowAddress(false);
        // }
        previousAddresses.forEach(p => {
            if (p.displayId == id) {
                let newAddressInfo: addressInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    streetNumber: p.streetNumber,
                    streetName: p.streetName,
                    streetType: p.streetType,
                    streetDirection: p.streetDirection,
                    city: p.city,
                    province: p.province,
                    country: p.country,
                    postalCode: p.postalCode,
                    unitType: p.unitType,
                    unitNumber: p.unitNumber,
                    manualAddressEntered: !p.manualAddressEntered,
                    yearsLivedOn: p.yearsLivedOn,
                    monthsLivedOn: p.monthsLivedOn
                }

                dispatch(setAddressInfo(newAddressInfo));
            }
        });
        console.log(previousAddresses);
    };

    useEffect(() => {
        document.title = "Where were you living previously?";

        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.applicant = applicant;
            result.homeAddress = homeAddress;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });

        if (state != null) {
            setEditMode(true);
        }
    }, []);


    const onError = (errors: object) => {
        console.log(errors);
        setClickInsightFormErrors(errors);
    }

    const addPrevAddress = () => {
        let addressId = (previousAddresses.length + 1).toString();
        let newAddress: addressInfo = {
            id: 0,
            displayId: addressId,
            streetNumber: "",
            streetName: "",
            streetType: "",
            streetDirection: "",
            unitType: "",
            unitNumber: "",
            city: "",
            province: "",
            postalCode: "",
            country: "",
            manualAddressEntered: false,
            yearsLivedOn: undefined,
            monthsLivedOn: undefined
        }
        dispatch(addPreviousAddress(newAddress));
    }

    const setResidenceLength = (id: string, years?: string, months?: string) => {
        previousAddresses.forEach(p => {
            if (p.displayId == id) {
                let newAddressInfo: addressInfo = {
                    id: p.id,
                    displayId: p.displayId,
                    streetNumber: p.streetNumber,
                    streetName: p.streetName,
                    streetType: p.streetType,
                    streetDirection: p.streetDirection,
                    city: p.city,
                    province: p.province,
                    country: p.country,
                    postalCode: p.postalCode,
                    unitType: p.unitType,
                    unitNumber: p.unitNumber,
                    manualAddressEntered: p.manualAddressEntered,
                    yearsLivedOn: years != undefined ? +years : p.yearsLivedOn,
                    monthsLivedOn: months != undefined ? +months : p.monthsLivedOn
                }
                dispatch(setAddressInfo(newAddressInfo));

                if (addressErrors[parseInt(id) - 1].includes("yearsLivedOn") && years != undefined && years.trim() != "") {
                    let newAddrErrors = [...addressErrors];
                    newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "yearsLivedOn");
                    setAddressErrors(newAddrErrors);
                }
                if (addressErrors[parseInt(id) - 1].includes("monthsLivedOn") && months != undefined && months.trim() != "") {
                    let newAddrErrors = [...addressErrors];
                    newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "monthsLivedOn");
                    setAddressErrors(newAddrErrors);
                }
            }
        });
    };

    useEffect(() => {
        let addrErrors: string[][] = [];
        let currentAddrErrors = {... addressErrors};
        previousAddresses.forEach((value: addressInfo, index: any) => {
            let id = index + 1;
            let fields = [
                {
                    element: "prevAddressSearch" + id,
                    field: "",
                    mode: window.pca?.fieldMode.SEARCH,
                },
                {
                    element: "span-street-address-" + id,
                    field: "Line1",
                    mode: window.pca?.fieldMode.POPULATE,
                },
                {
                    element: "span-street-address2-" + id,
                    field: "Line2",
                    mode: window.pca?.fieldMode?.POPULATE,
                },
                { element: "city-" + id, field: "City", mode: window.pca?.fieldMode.POPULATE },
                {
                    element: "span-state-" + id,
                    field: "ProvinceName",
                    mode: window.pca?.fieldMode.POPULATE,
                },
                {
                    element: "",
                    field: "PostalCode",
                    mode: window.pca?.fieldMode.POPULATE,
                },
                {
                    element: "span-country-" + id,
                    field: "CountryName",
                    mode: window.pca?.fieldMode.POPULATE,
                },
            ];
            let control = new window.pca.Address(fields, options);
            control.listen("populate", (address: any) => {
                let streetInfo = address.Line1.split(" ");
                let streetName = "";
                let streetDirection = "";
                let streetType = "";
                let streetComponents: string[] = address.Street.split(" ");
                let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

                //Check if street direction and type exists in the street name
                if (streetDirections.includes(lastStreetComponent)) {
                    streetDirection = lastStreetComponent;
                    let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
                    if (streetTypes.includes(secondLastStreetComponent)) {
                        streetType = secondLastStreetComponent;
                    }
                    streetComponents.forEach((value, index) => {
                        if (index < streetComponents.length - 2) {
                            streetName += value + " "
                        }
                    });
                } else if (streetTypes.includes(lastStreetComponent)) {
                    streetType = lastStreetComponent;
                    streetComponents.forEach((value, index) => {
                        if (index != streetComponents.length - 1) {
                            streetName += value + " "
                        }
                    });
                } else {
                    streetComponents.forEach((value, index) => {
                        console.log(value, index);
                        streetName += value + " "
                    });
                }
                streetName = streetName.trim();

                //Check if unit number is embeded in the street number
                let streetNumber = "";
                let streetNumberComponents = streetInfo[0].split("-");
                if (streetNumberComponents.length > 1) {
                    streetNumber = streetNumberComponents[streetNumberComponents.length - 1];
                } else {
                    streetNumber = streetInfo[0];
                }

                let newAddressInfo: addressInfo = {
                    displayId: id.toString(),
                    id: value.id,
                    streetNumber: streetNumber,
                    streetName: streetName,
                    streetType: streetType,
                    streetDirection: streetDirection,
                    city: address.City,
                    province: address.ProvinceCode,
                    country: address.CountryName,
                    postalCode: address.PostalCode,
                    unitType: address.SubBuilding != undefined && address.SubBuilding != "" ? "unit" : "",
                    unitNumber: address.SubBuilding != undefined ? address.SubBuilding : "",
                    manualAddressEntered: value.manualAddressEntered,
                    yearsLivedOn: value.yearsLivedOn,
                    monthsLivedOn: value.monthsLivedOn
                }
                dispatch(setAddressInfo(newAddressInfo));
            });
            if (addressErrors[index] != undefined) {
                if (value.streetNumber.trim() != "") {
                    currentAddrErrors[parseInt(id) - 1] = currentAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "streetNumber");
                }
                if (value.streetName.trim() != "") {
                    currentAddrErrors[parseInt(id) - 1] = currentAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "streetName");
                }
                if (value.streetType.trim() != "") {
                    currentAddrErrors[parseInt(id) - 1] = currentAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "streetType");
                }
                if (value.city.trim() != "") {
                    currentAddrErrors[parseInt(id) - 1] = currentAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "city");
                }
                if (value.country.trim() != "") {
                    currentAddrErrors[parseInt(id) - 1] = currentAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "country");
                }
                if (value.province.trim() != "") {
                    currentAddrErrors[parseInt(id) - 1] = currentAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "province");
                }
                if (value.streetNumber.trim() != "" && value.streetName.trim() != "" && value.streetType.trim() != "" && value.city.trim() != ""
                    && value.country.trim() != "" && value.province.trim() != "") {
                    currentAddrErrors[parseInt(id) - 1] = currentAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "autoAddress");
                }
                addrErrors.push(currentAddrErrors[index]);
            } else {
                addrErrors.push([]);
            }
        });
        setAddressErrors(addrErrors);

    }, [previousAddresses])

    const getAddressData = (id: any, type: any, val: any) => {
        let addressData = { ...previousAddresses[id - 1] };

        switch (type) {
            case "streetNumber":
                addressData.streetNumber = val;
                break;
            case "streetName":
                addressData.streetName = val;
                break;
            case "streetType":
                addressData.streetType = val;
                break;
            case "streetDirection":
                addressData.streetDirection = val;
                break;
            case "unitNumber":
                addressData.unitNumber = val;
                break;
            case "unitType":
                addressData.unitType = val;
                break;
            case "cityName":
                addressData.city = val;
                break;
            case "province":
                addressData.province = val;
                break;
            case "postalCode":
                addressData.postalCode = val;
                break;
            case "country":
                addressData.country = val;
                //Clear the province/state if country is updated
                addressData.province = "";
                break;
        }

        dispatch(setAddressInfo(addressData));

        let newAddrErrors = { ...addressErrors };
        if (addressErrors[parseInt(id) - 1].includes("streetNumber") && type == "streetNumber" && val.trim() != "") {
            newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "streetNumber");
        }
        if (addressErrors[parseInt(id) - 1].includes("streetName") && type == "streetName" && val.trim() != "") {
            newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "streetName");
        }
        if (addressErrors[parseInt(id) - 1].includes("streetType") && type == "streetType" && val.trim() != "") {
            newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "streetType");
        }
        if (addressErrors[parseInt(id) - 1].includes("city") && type == "cityName" && val.trim() != "") {
            newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "city");
        }
        if (addressErrors[parseInt(id) - 1].includes("country") && type == "country" && val.trim() != "") {
            newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "country");
        }
        if (addressErrors[parseInt(id) - 1].includes("province") && type == "province" && val.trim() != "") {
            newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "province");
        }

        //If Street Number, Steet Name and City are Filled, Clear AutoAddress Error (if it exists)
        if (addressData.streetNumber.trim() != "" && addressData.streetName.trim() != ""
            && addressData.streetType.trim() != "" && addressData.city.trim() != "") {
            newAddrErrors[parseInt(id) - 1] = newAddrErrors[parseInt(id) - 1].filter((error: string) => error !== "autoAddress");
        }
        setAddressErrors(newAddrErrors);
    }

    const previousAddressList = () => {
        return (
            <AnimatePresence>
                {previousAddresses.map((position: any) => (
                    <motion.div
                        key={position.displayId}
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div className="col-span-1 mb-4 mt-5">
                            <div className={""}>
                                <label htmlFor="search">Search for an address</label>
                                <div className="flex md:flex-row flex-col justify-between">
                                    <div className="flex relative">
                                        <span>
                                            <img
                                                src="assets/magnifying-glass-icon.svg"
                                                className={clsx(
                                                    methods.formState?.errors?.autoAddress || addressErrors[position.displayId - 1]?.includes("autoAddress")
                                                        ? "hidden"
                                                        : "",
                                                    "w-5 h-5 absolute top-5 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                )}
                                                alt="search"
                                                height={5}
                                                width={5}
                                            />
                                            <img
                                                src="assets/error-icon.svg"
                                                className={clsx(
                                                    methods.formState?.errors?.autoAddress || addressErrors[position.displayId - 1]?.includes("autoAddress")
                                                        ? ""
                                                        : "hidden",
                                                    "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                )}
                                                alt="error"
                                                height={5}
                                                width={5}
                                            />
                                        </span>
                                        <input
                                            autoComplete="nope"
                                            className={clsx(
                                                methods.formState?.errors?.autoAddress?.message || addressErrors[position.displayId - 1]?.includes("autoAddress")
                                                    ? "error"
                                                    : "",
                                                "mt-2"
                                            )}
                                            id={"prevAddressSearch" + position.displayId}
                                            type="text"
                                            placeholder="Start typing address"
                                        />
                                    </div>
                                    {position.displayId != 1 && <div className="mb-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                dispatch(removePreviousAddress(position.displayId));
                                            }}
                                            className="secondary mt-2 w-full sm:w-28 h-11 px-6 ml-3"
                                            style={{ width: '-webkit-fill-available', color: 'var(--brand-blue)', background: "transparent" }}>
                                            <img
                                                className="mr-3"
                                                src="assets/remove-item.svg"
                                                alt="tooltip"
                                                width={20}
                                                height={20}
                                            />
                                            Remove
                                        </button>
                                    </div>}
                                </div>
                                <div className="flex relative mt-3">
                                    <p className="active underline cursor-pointer" onClick={() => toggleManualAddress(position.displayId)}>Enter address manually</p>
                                </div>

                                {addressSelectionError && (
                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                        You must select an address from the list of options
                                    </span>
                                )}
                                <div className={clsx(position.city != "" ? "" : "hidden")}>
                                    <p id="formatted-address"></p>
                                    {position.address != "" && <><span >{position.streetNumber} {position.streetName} {position.streetType}
                                        {position.streetDirection != "" && " " + position.streetDirection}</span><br /></>}
                                    {position.city != "" && <><span >{position.city}</span> &nbsp;</>}
                                    {position.province != "" && <><span >{position.province}</span>&nbsp;</>}
                                    {position.postalCode != "" && <><span >{position.postalCode}</span><br /></>}
                                    {position.country != "" && <span >{position.country}</span>}
                                </div>
                                <div
                                    className={clsx(
                                        position.manualAddressEntered ? "" : "hidden",
                                        "flex relative"
                                    )}
                                >
                                    <div className="mt-5">
                                        <ManualAddress passAddressData={getAddressData} addressData={position} addressErrors={addressErrors} errorIndex={position.displayId - 1} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={clsx(
                            position.city != ""
                                ? "mt-5"
                                : "",
                            "flex md:flex-row flex-col w-max mb-5"
                        )}>
                            <div className="">
                                <label htmlFor="title">How long have you lived at this address?</label>
                                <div className="flex relative">
                                    <span>
                                        <img
                                            src="assets/error-icon.svg"
                                            className={clsx(
                                                addressErrors[position.displayId - 1]?.includes("yearsLivedOn") ? "" : "hidden",
                                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[120px] xs:max-md:ml-[370px] md:ml-[140px]"
                                            )}
                                            alt="error"
                                            height={5}
                                            width={5}
                                        />
                                    </span>
                                    <div className="flex">
                                        <input
                                            className={clsx(addressErrors[position.displayId - 1]?.includes("yearsLivedOn")
                                                ? "error"
                                                : "",
                                                "mt-2", style.residentPeriod
                                            )}
                                            type="number"
                                            min={0}
                                            placeholder="year(s)"
                                            id="residentLengthYears"
                                            value={position != undefined ? position.yearsLivedOn : ""}
                                            onChange={(e) => {
                                                if (e.target.value.charAt(0) == "0") {
                                                    e.target.value = e.target.value.slice(1, 2);
                                                } else {
                                                    e.target.value = e.target.value.slice(0, 2);
                                                }
                                                setResidenceLength(position.displayId, e.target.value)
                                            }}
                                        // {...methods.register("residenceYears", {
                                        //     required: "Enter the number of years you lived at this address.",
                                        // })}
                                        />
                                        {position.yearsLivedOn != undefined &&
                                            <span style={{ position: "absolute", top: "19px", left: position.yearsLivedOn?.toString().length > 1 ? "30px" : "22px" }}>year{position.yearsLivedOn != 1 ? "s" : ""}</span>
                                        }
                                    </div>
                                    <span>
                                        <img
                                            src="assets/error-icon.svg"
                                            className={clsx(
                                                addressErrors[position.displayId - 1]?.includes("monthsLivedOn") ? "" : "hidden",
                                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[120px] xs:max-md:ml-[370px] md:ml-[140px]"
                                            )}
                                            alt="error"
                                            height={5}
                                            width={5}
                                        />
                                    </span>
                                    <div className="flex">
                                        <input
                                            className={clsx(addressErrors[position.displayId - 1]?.includes("monthsLivedOn")
                                                ? "error"
                                                : "",
                                                "mt-2 ml-2", style.residentPeriod
                                            )}
                                            type="number"
                                            placeholder="month(s)"
                                            min={0}
                                            max={11}
                                            id="residentLengthMonths"
                                            value={position != undefined ? position.monthsLivedOn : ""}
                                            onChange={(e) => {
                                                if (e.target.value.charAt(0) == "0") {
                                                    e.target.value = e.target.value.slice(1, 2);
                                                } else {
                                                    e.target.value = e.target.value.slice(0, 2);
                                                }
                                                if (+e.target.value > 11) {
                                                    e.target.value = "11"
                                                }
                                                setResidenceLength(position.displayId, undefined, e.target.value)
                                            }}
                                        // {...methods.register("residenceMonths", {
                                        //     required: "Enter the number of months you lived at this address.",
                                        // })}
                                        />
                                        {position.monthsLivedOn != undefined &&
                                            <span style={{ position: "absolute", top: "19px", left: position.monthsLivedOn.toString().length > 1 ? "203px" : "195px" }}>month{position.monthsLivedOn != 1 ? "s" : ""}</span>
                                        }
                                    </div>
                                </div>
                                {(addressErrors[position.displayId - 1]?.includes("yearsLivedOn") || addressErrors[position.displayId - 1]?.includes("monthsLivedOn")) && (
                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                        Enter your time at this address
                                    </span>
                                )}
                            </div>
                        </div>
                        <hr className="mt-5" />
                    </motion.div>
                ))}
            </AnimatePresence>
        )
    };

    const onSubmit = async (data: any) => {
        let totalOccupancyTime = (applicant.residenceYears * 12) + applicant.residenceMonths;
        let emptyFieldExists = false;

        let addrErrors = { ...addressErrors };
        // let errors = addressErrors[0];
        // errors.push("autoAddress");
        // addrErrors[0] = errors;
        // setAddressErrors(addrErrors);

        previousAddresses.forEach((p, index) => {
            let errors = [];
            totalOccupancyTime += (p.yearsLivedOn != undefined ? (p.yearsLivedOn * 12) : 0) + (p.monthsLivedOn != undefined ? p.monthsLivedOn : 0)
            if (!p.manualAddressEntered && (p.streetNumber.trim() == "" || p.streetName.trim() == "" || p.city.trim() == "" || p.country.trim() == "")) {
                emptyFieldExists = true;
                errors.push("autoAddress");
            }
            if (p.streetNumber.trim() == "") {
                emptyFieldExists = true;
                errors.push("streetNumber");
            }
            if (p.streetType.trim() == "") {
                emptyFieldExists = true;
                errors.push("streetType");
            }
            if (p.streetName.trim() == "") {
                emptyFieldExists = true;
                errors.push("streetName");
            }
            if (p.unitType.trim() != "" && p.unitNumber.trim() == "") {
                emptyFieldExists = true;
                errors.push("unitNumber");
            }
            if (p.unitNumber.trim() != "" && p.unitType.trim() == "") {
                emptyFieldExists = true;
                errors.push("unitType");
            }
            if (p.city.trim() == "") {
                emptyFieldExists = true;
                errors.push("city");
            }
            if (p.province.trim() == "" && (p.country.trim() == "Canada" || p.country.trim() == "United States")) {
                emptyFieldExists = true;
                errors.push("province");
            }
            if (p.postalCode.trim() == "" && (p.country.trim() == "Canada" || p.country.trim() == "United States")) {
                emptyFieldExists = true;
                errors.push("postalCode");
            }
            if (p.country.trim() == "") {
                emptyFieldExists = true;
                errors.push("country");
            }
            if (p.yearsLivedOn == undefined) {
                emptyFieldExists = true;
                errors.push("yearsLivedOn");
            }
            if (p.monthsLivedOn == undefined) {
                emptyFieldExists = true;
                errors.push("monthsLivedOn");
            }
            addrErrors[index] = errors;
            setAddressErrors(addrErrors);
            if (errors.length > 0) {
                toggleManualAddress(p.displayId);
            }
        });
        if (totalOccupancyTime < 36) {
            setErrorMessage("Three years of address history is required.");
            setDisplayError(true);
            window.scroll(0, 0);
            return;
        }
        if (emptyFieldExists) {
            setErrorMessage("One or more fields is missing in your address(es).");
            setDisplayError(true);
            window.scroll(0, 0);
            return;
        }
        setDisplayError(false);
        dispatch(setApplicationProgress(10));

        //Update Addresses in Trellis Backend
        //TODO: Avoid unnecessary call to backend by performing local comparison

        let mainAddressList: AddressDto[] = [];
        previousAddresses.forEach(previousAddress => {
            let newAddress = new AddressDto({
                id: previousAddress.id ? previousAddress.id : 0,
                streetNumber: previousAddress.streetNumber,
                streetName: previousAddress.streetName,
                streetType: previousAddress.streetType,
                streetDirection: previousAddress.streetDirection,
                unitType: previousAddress.unitType,
                unitNumber: previousAddress.unitNumber,
                city: previousAddress.city,
                region: previousAddress.province,
                postalCode: previousAddress.postalCode,
                country: previousAddress.country,
                timeAtResidenceInMonths: (previousAddress.yearsLivedOn ? +previousAddress.yearsLivedOn * 12 : 0)
                    + (previousAddress.monthsLivedOn ? +previousAddress.monthsLivedOn : 0),
                residenceStatusType_Name: undefined,
                deliveryMode: undefined,
                modeNumber: undefined,
                deliveryMode2: undefined,
                modeNumber2: undefined,
                isMailing: false
            });
            mainAddressList.push(newAddress);
        });

        //Add Current Address to Prevent Deletion
        mainAddressList.push(new AddressDto({
            id: homeAddress.id,
            streetNumber: homeAddress.streetNumber,
            streetName: homeAddress.streetName,
            streetType: homeAddress.streetType,
            streetDirection: homeAddress.streetDirection,
            unitType: homeAddress.unitType,
            unitNumber: homeAddress.unitNumber,
            city: homeAddress.city,
            region: homeAddress.province,
            postalCode: homeAddress.postalCode,
            country: homeAddress.country,
            timeAtResidenceInMonths: (+applicant.residenceYears * 12) + +applicant.residenceMonths,
            residenceStatusType_Name: applicant.residentStatus,
            deliveryMode: undefined,
            modeNumber: undefined,
            deliveryMode2: undefined,
            modeNumber2: undefined,
            isMailing: false
        }));

        setIsSaving(true);
        await client.apiApplicantsAddressesPost(mainAddressList).then((result) => {
            console.log("Trellis Addresses Added");
            result.forEach((address) => {
                previousAddresses.forEach(p => {
                    if (p.streetNumber == address.streetNumber && p.streetName == address.streetName) {
                        let newAddressInfo: addressInfo = {
                            displayId: p.displayId,
                            id: address.id ? address.id : 0,
                            streetNumber: p.streetNumber,
                            streetName: p.streetName,
                            streetType: p.streetType,
                            streetDirection: p.streetDirection,
                            city: p.city,
                            province: p.province,
                            country: p.country,
                            postalCode: p.postalCode,
                            unitType: p.unitType,
                            unitNumber: p.unitNumber,
                            manualAddressEntered: p.manualAddressEntered,
                            yearsLivedOn: p.yearsLivedOn,
                            monthsLivedOn: p.monthsLivedOn
                        }
                        dispatch(setAddressInfo(newAddressInfo));
                    }
                });
            });
            setIsSaving(false);
            if (editMode) {
                navigate(-1);
            } else {
                navigate("/joint/employment");
            }

        }).catch((error) => {
            console.log("Error updating trellis address");
            setIsSaving(false);
            if (editMode) {
                navigate(-1);
            } else {
                navigate("/joint/employment");
            }
        });
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <h1 aria-label="Address Header">Where were you living previously?</h1>
                        <p className="sub-heading">Tell us about your previous living situation. We are looking for <b>three years</b> of address history.</p>
                        {editMode && <div className="flex md:flex-row flex-col">
                            <div className={style.twoColFirst}>
                                <p className="mt-5">Your current address</p>
                                <p className={style.addressDetails + " mt-3"}>
                                    {homeAddress?.unitNumber ? homeAddress?.unitNumber + "-" : ""}
                                    {homeAddress.streetNumber} {homeAddress.streetName},{" "}
                                </p>
                                <p className={style.addressDetails}>
                                    {homeAddress.city} {homeAddress.province}{" "}
                                    {homeAddress.postalCode}
                                </p>
                                <p className={style.addressDetails}>{homeAddress.country}</p>
                            </div>
                            <div>
                                <p className="mt-5 text-sm">How long have you lived at this address?</p>
                                <p className={style.addressDetails + " mt-3"}>
                                    {applicant.residenceYears} Year{applicant.residenceYears != 1 && "s"} & {applicant.residenceMonths} Month{applicant.residenceMonths != 1 && "s"}
                                </p>
                            </div>
                        </div>}
                        {displayError &&
                            <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                <img src="assets/error-info-icon.svg" alt="error" />
                                <span className="text-sm ml-2 mt-1">
                                    {errorMessage}
                                </span>
                            </div>
                        }
                        <div className={"flex flex-row"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    {previousAddresses != undefined && previousAddresses.length > 0 &&
                                        <>
                                            {previousAddressList()}
                                        </>

                                    }

                                    <div className="mb-5">
                                        <button
                                            type="button"
                                            onClick={addPrevAddress}
                                            className="secondary mt-5 w-full sm:w-28 h-11 px-6"
                                            style={{ width: '-webkit-fill-available', color: 'var(--brand-blue)' }}>
                                            <img
                                                className="mr-3"
                                                style={{ marginBottom: 2 }}
                                                src="assets/add-circle.svg"
                                                alt="tooltip"
                                                width={15}
                                                height={15}
                                            />Add Another Address
                                        </button>
                                    </div>
                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                {editMode ? "Cancel" : "Back"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode ? "Save" : "Continue"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode ? "Save" : "Continue"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                {editMode ? "Cancel" : "Back"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}